/**
 * Easing functions for smooth animations
 * Based on Robert Penner's easing functions
 */

import { EasingFunction } from './animationTypes';

export const easings: Record<string, EasingFunction> = {
    // Linear
    linear: (t: number) => t,

    // Quadratic
    easeInQuad: (t: number) => t * t,
    easeOutQuad: (t: number) => t * (2 - t),
    easeInOutQuad: (t: number) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t,

    // Cubic
    easeInCubic: (t: number) => t * t * t,
    easeOutCubic: (t: number) => (--t) * t * t + 1,
    easeInOutCubic: (t: number) => t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1,

    // Quartic
    easeInQuart: (t: number) => t * t * t * t,
    easeOutQuart: (t: number) => 1 - (--t) * t * t * t,
    easeInOutQuart: (t: number) => t < 0.5 ? 8 * t * t * t * t : 1 - 8 * (--t) * t * t * t,

    // Quintic
    easeInQuint: (t: number) => t * t * t * t * t,
    easeOutQuint: (t: number) => 1 + (--t) * t * t * t * t,
    easeInOutQuint: (t: number) => t < 0.5 ? 16 * t * t * t * t * t : 1 + 16 * (--t) * t * t * t * t,

    // Sine
    easeInSine: (t: number) => 1 - Math.cos((t * Math.PI) / 2),
    easeOutSine: (t: number) => Math.sin((t * Math.PI) / 2),
    easeInOutSine: (t: number) => -(Math.cos(Math.PI * t) - 1) / 2,

    // Exponential
    easeInExpo: (t: number) => t === 0 ? 0 : Math.pow(2, 10 * t - 10),
    easeOutExpo: (t: number) => t === 1 ? 1 : 1 - Math.pow(2, -10 * t),
    easeInOutExpo: (t: number) => {
        if (t === 0) return 0;
        if (t === 1) return 1;
        if (t < 0.5) return Math.pow(2, 20 * t - 10) / 2;
        return (2 - Math.pow(2, -20 * t + 10)) / 2;
    },

    // Circular
    easeInCirc: (t: number) => 1 - Math.sqrt(1 - Math.pow(t, 2)),
    easeOutCirc: (t: number) => Math.sqrt(1 - Math.pow(t - 1, 2)),
    easeInOutCirc: (t: number) => {
        if (t < 0.5) return (1 - Math.sqrt(1 - Math.pow(2 * t, 2))) / 2;
        return (Math.sqrt(1 - Math.pow(-2 * t + 2, 2)) + 1) / 2;
    },

    // Elastic
    easeInElastic: (t: number) => {
        if (t === 0) return 0;
        if (t === 1) return 1;
        const c4 = (2 * Math.PI) / 3;
        return -Math.pow(2, 10 * t - 10) * Math.sin((t * 10 - 10.75) * c4);
    },
    easeOutElastic: (t: number) => {
        if (t === 0) return 0;
        if (t === 1) return 1;
        const c4 = (2 * Math.PI) / 3;
        return Math.pow(2, -10 * t) * Math.sin((t * 10 - 0.75) * c4) + 1;
    },
    easeInOutElastic: (t: number) => {
        if (t === 0) return 0;
        if (t === 1) return 1;
        const c5 = (2 * Math.PI) / 4.5;
        if (t < 0.5) {
            return -(Math.pow(2, 20 * t - 10) * Math.sin((20 * t - 11.125) * c5)) / 2;
        }
        return (Math.pow(2, -20 * t + 10) * Math.sin((20 * t - 11.125) * c5)) / 2 + 1;
    },

    // Back
    easeInBack: (t: number) => {
        const c1 = 1.70158;
        const c3 = c1 + 1;
        return c3 * t * t * t - c1 * t * t;
    },
    easeOutBack: (t: number) => {
        const c1 = 1.70158;
        const c3 = c1 + 1;
        return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
    },
    easeInOutBack: (t: number) => {
        const c1 = 1.70158;
        const c2 = c1 * 1.525;
        if (t < 0.5) {
            return (Math.pow(2 * t, 2) * ((c2 + 1) * 2 * t - c2)) / 2;
        }
        return (Math.pow(2 * t - 2, 2) * ((c2 + 1) * (t * 2 - 2) + c2) + 2) / 2;
    },

    // Bounce
    easeInBounce: (t: number) => 1 - easings.easeOutBounce(1 - t),
    easeOutBounce: (t: number) => {
        const n1 = 7.5625;
        const d1 = 2.75;

        if (t < 1 / d1) {
            return n1 * t * t;
        } else if (t < 2 / d1) {
            return n1 * (t -= 1.5 / d1) * t + 0.75;
        } else if (t < 2.5 / d1) {
            return n1 * (t -= 2.25 / d1) * t + 0.9375;
        } else {
            return n1 * (t -= 2.625 / d1) * t + 0.984375;
        }
    },
    easeInOutBounce: (t: number) => {
        if (t < 0.5) return easings.easeInBounce(t * 2) / 2;
        return easings.easeOutBounce(t * 2 - 1) / 2 + 0.5;
    },

    // Shortcuts
    easeIn: (t: number) => easings.easeInQuad(t),
    easeOut: (t: number) => easings.easeOutQuad(t),
    easeInOut: (t: number) => easings.easeInOutQuad(t)
};

/**
 * Create a custom bezier easing function
 */
export function cubicBezier(x1: number, y1: number, x2: number, y2: number): EasingFunction {
    return (t: number) => {
        // This is a simplified version. For production, use a proper bezier implementation
        const cx = 3 * x1;
        const bx = 3 * (x2 - x1) - cx;
        const ax = 1 - cx - bx;

        const cy = 3 * y1;
        const by = 3 * (y2 - y1) - cy;
        const ay = 1 - cy - by;

        const sampleCurveX = (t: number) => ((ax * t + bx) * t + cx) * t;
        const sampleCurveY = (t: number) => ((ay * t + by) * t + cy) * t;

        // Newton-Raphson iteration
        let x = t;
        for (let i = 0; i < 4; i++) {
            const currentX = sampleCurveX(x) - t;
            const currentSlope = (3 * ax * x + 2 * bx) * x + cx;
            if (Math.abs(currentX) < 0.001) break;
            x -= currentX / currentSlope;
        }

        return sampleCurveY(x);
    };
}

/**
 * Common cubic-bezier presets
 */
export const bezierPresets = {
    ease: cubicBezier(0.25, 0.1, 0.25, 1),
    easeIn: cubicBezier(0.42, 0, 1, 1),
    easeOut: cubicBezier(0, 0, 0.58, 1),
    easeInOut: cubicBezier(0.42, 0, 0.58, 1),
    // Material Design
    standardCurve: cubicBezier(0.4, 0, 0.2, 1),
    decelerationCurve: cubicBezier(0, 0, 0.2, 1),
    accelerationCurve: cubicBezier(0.4, 0, 1, 1),
    sharpCurve: cubicBezier(0.4, 0, 0.6, 1)
};